import { type ReactNode } from 'react'
import {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarHeader,
    SidebarInset,
    SidebarMenu,
    SidebarMenuItem,
    useSidebar,
    Button,
    Toaster,
    cn,
} from '@duck/ui'
import { Logo } from '../Misc/Logo'
import { Link } from '@tanstack/react-router'
import { LeftArrowLogical, RightArrowLogical } from '@duck/ui'
import { SidebarUserArea } from '../layout/SidebarUserArea'
import { useEventEmitter, useTar } from '@/modules'
import { SideBarMenu } from './SideBarMenu'
import React from 'react'
import { useAuth } from '@/hooks/useAuth'

type EnvSideBarProps = {
    children: ReactNode
}

export const SideBarLayout = ({ children }: EnvSideBarProps) => {
    const { open, toggleSidebar, openMobile, isMobile, setOpenMobile } = useSidebar()
    const { isAuthenticated } = useAuth()
    const { isRtl } = useTar()
    const emitChartsTriggerRedraw = useEventEmitter('charts:triggerRedraw', 10)

    // Handler to close mobile sidebar when navigating
    const handleMobileNav = () => {
        if (isMobile) {
            setOpenMobile(false)
        }
    }
    return (
        <React.Fragment key={`${isAuthenticated ? 'authenticated' : 'unauthenticated'}`}>
            {/* Mobile Burger Button - Fixed position, only visible on mobile */}
            {isMobile && (
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={toggleSidebar}
                    className="fixed top-4 start-4 z-50 md:hidden bg-background/80 backdrop-blur-sm border border-border shadow-md"
                    aria-label="Toggle Sidebar"
                >
                    <div className="w-5 h-5 flex flex-col justify-center items-center">
                        <span className={`bg-current h-0.5 w-5 rounded transition-all duration-300 ${openMobile ? 'rotate-45 translate-y-0.5' : '-translate-y-1'}`} />
                        <span className={`bg-current h-0.5 w-5 rounded transition-all duration-300 ${openMobile ? 'opacity-0' : 'opacity-100'}`} />
                        <span className={`bg-current h-0.5 w-5 rounded transition-all duration-300 ${openMobile ? '-rotate-45 -translate-y-0.5' : 'translate-y-1'}`} />
                    </div>
                </Button>
            )}

            <div className="group/sidebar-expand relative">
                <Sidebar collapsible="icon" variant="sidebar">
                    <SidebarHeader>
                        <SidebarMenu>
                            <SidebarMenuItem className={`w-full ${open ? 'py-2 px-2' : 'py-1 px-0.5'}`}>
                                <div className="flex items-center justify-between w-full">
                                    <Link to="/" onClick={handleMobileNav} className={`flex text-[22px] ${!open ? 'ms-1' : ''}`}>
                                        <Logo short={!open} />
                                    </Link>
                                </div>
                            </SidebarMenuItem>
                        </SidebarMenu>
                    </SidebarHeader>
                    <SidebarContent className='custom-scrollbar'>
                        <SideBarMenu open={open} handleMobileNav={handleMobileNav} />
                    </SidebarContent>

                    <SidebarFooter>
                        <SidebarMenu>
                            <SidebarMenuItem>
                                <SidebarUserArea />
                            </SidebarMenuItem>
                        </SidebarMenu>
                    </SidebarFooter>
                </Sidebar>

                {/* Toggle button - appears on border hover */}
                {!isMobile && (
                    <Button
                        variant="outline"
                        size="icon"
                        onClick={() => {
                            toggleSidebar()
                            setTimeout(() => {
                                emitChartsTriggerRedraw({})
                            }, 300)
                        }}
                        className={cn(`absolute top-12 -translate-y-1/2 ${isRtl ? '-end-6' : 'end-0'} translate-x-1/2 z-20 h-6 w-6 rounded-full opacity-0 group-hover/sidebar-expand:opacity-100 transition-opacity bg-background border border-primary shadow-md`)}
                        aria-label={open ? "Collapse Sidebar" : "Expand Sidebar"}
                    >
                        {open ? <LeftArrowLogical className="h-3 w-3" /> : <RightArrowLogical className="h-3 w-3" />}
                    </Button>
                )}
            </div>

            <SidebarInset>
                <main
                    style={isMobile ? undefined : {
                        maxWidth: open ? 'calc(100dvw - 255px)' : 'calc(100dvw - 63px)',
                    }}
                    className="flex h-full flex-1 flex-col overflow-y-auto custom-scrollbar max-h-screen relative" id="main-content">
                    <Toaster style={{ position: 'absolute' }}
                        className="flex justify-center max-w-[calc(100dvw-32px)]"
                        toastOptions={{
                            style: { width: "fit-content", marginInline: "auto" },
                            classNames: {
                                actionButton: "!bg-primary !text-primary-foreground hover:!bg-primary/90",
                            },
                        }} />
                    {children}
                </main>
            </SidebarInset>
        </React.Fragment>
    )
}

